import { css } from '@emotion/react'
import { observer } from 'mobx-react'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { routeName } from '~/@common/constants'
import { useRepository } from '~/@common/hooks/useRepository'
import { errorHandlerService } from '~/@common/services'
import { commonRepo } from '~/@common/services/repo.service'
import { mediaQuery } from '~/@common/styles/mediaQuery'

import WeeklyHistoryService from './@service/WeeklyHistory.service'
import AttendanceSection from './@widgets/Attendance/AttendanceSection'
import ParentRecentStudySection from './@widgets/ParentRecentStudy/ParentRecentStudySection'
import WeeklyChart from './@widgets/WeeklySummary/WeeklyChart'
import WeeklyStats from './@widgets/WeeklySummary/WeeklyStats'

/**
 * 학부모 학습홈
 * PC: 2컬럼 — 좌(출석+학습정보+차트), 우(학습 카드 리스트)
 * 모바일: 1컬럼 세로 스택
 */
const ParentHomePage = () => {
  const weeklyService = useRepository(WeeklyHistoryService)
  const studentId = commonRepo.parent.activeChildStudentId
  const navigate = useNavigate()

  const { byFeature } = commonRepo.permission
  const isAttendanceEnabled = byFeature.isAllowed('ATTENDANCE')
  const isFourWeeksChartEnabled = byFeature.isAllowed('LEARNING_FOUR_WEEKS_PROBLEM')

  useEffect(() => {
    if (!studentId) return
    weeklyService.reset()
    weeklyService.load(studentId).catch((err) => errorHandlerService.handle(err))
  }, [studentId, weeklyService])

  if (!studentId) return null

  return (
    <div css={_Style}>
      <div className="parent-left-col">
        <div className="parent-info-row">
          {isAttendanceEnabled && studentId && (
            <AttendanceSection
              studentId={studentId}
              onPastAttendanceClick={() => navigate(routeName.parent.attendance)}
            />
          )}
          <WeeklyStats
            rate={weeklyService.correctRate}
            totalCount={weeklyService.totalProblemCount}
          />
        </div>
        {isFourWeeksChartEnabled && <WeeklyChart data={weeklyService.chartData} />}
      </div>
      <div className="parent-right-col">
        <ParentRecentStudySection />
      </div>
    </div>
  )
}

export default observer(ParentHomePage)

const _Style = css`
  display: flex;
  gap: 24px;
  height: 100%;

  .parent-left-col {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 24px;
    min-height: 0;
    min-width: 0;
  }

  .parent-info-row {
    display: flex;
    gap: 24px;
  }

  .parent-info-row > * {
    flex: 1;
    min-width: 0;
  }

  .parent-right-col {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
  }

  ${mediaQuery.underTablet} {
    flex-direction: column;
    gap: 20px;
    padding: 20px 16px 40px;
    overflow-y: auto;
    height: auto;

    .parent-left-col,
    .parent-right-col {
      flex: none;
      overflow: visible;
      min-height: auto;
    }

    .parent-info-row {
      flex-direction: column;
      gap: 20px;
    }
  }
`
